import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/cards/StatCard';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentActivities: [],
    upcomingEvents: []
  });

  useEffect(() => {
    // Mock data for admin dashboard
    setDashboardData({
      stats: {
        students: 1250,
        teachers: 85,
        notices: 42,
        admissions: 156
      },
      recentActivities: [
        { id: 1, activity: 'New student registered', user: 'John Doe', time: '2 minutes ago', type: 'student' },
        { id: 2, activity: 'New notice published', user: 'Admin', time: '15 minutes ago', type: 'notice' },
        { id: 3, activity: 'New teacher joined', user: 'Jane Smith', time: '1 hour ago', type: 'teacher' },
        { id: 4, activity: 'Result published', user: 'Math Dept', time: '2 hours ago', type: 'result' },
        { id: 5, activity: 'Admission request received', user: 'Robert Johnson', time: '3 hours ago', type: 'admission' }
      ],
      upcomingEvents: [
        { id: 1, event: 'Annual Day Celebration', date: '2023-12-25', time: '10:00 AM' },
        { id: 2, event: 'Science Fair', date: '2024-01-15', time: '09:00 AM' },
        { id: 3, event: 'Parents Meeting', date: '2024-01-20', time: '03:00 PM' }
      ]
    });
  }, []);

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            title="Total Students" 
            value={dashboardData.stats.students} 
            icon="🎓" 
            color="blue"
          />
          <StatCard 
            title="Total Teachers" 
            value={dashboardData.stats.teachers} 
            icon="👨‍🏫" 
            color="green"
          />
          <StatCard 
            title="Total Notices" 
            value={dashboardData.stats.notices} 
            icon="📢" 
            color="yellow"
          />
          <StatCard 
            title="Admissions" 
            value={dashboardData.stats.admissions} 
            icon="📝" 
            color="purple"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-3">
                <a href="/admin/notices" className="bg-primary text-white p-3 rounded text-center hover:bg-blue-700 transition">
                  <div className="text-2xl mb-1">📢</div>
                  <div className="text-sm">Notices</div>
                </a>
                <a href="/admin/students" className="bg-green-500 text-white p-3 rounded text-center hover:bg-green-600 transition">
                  <div className="text-2xl mb-1">🎓</div>
                  <div className="text-sm">Students</div>
                </a>
                <a href="/admin/teachers" className="bg-blue-500 text-white p-3 rounded text-center hover:bg-blue-600 transition">
                  <div className="text-2xl mb-1">👨‍🏫</div>
                  <div className="text-sm">Teachers</div>
                </a>
                <a href="/admin/results" className="bg-yellow-500 text-white p-3 rounded text-center hover:bg-yellow-600 transition">
                  <div className="text-2xl mb-1">📊</div>
                  <div className="text-sm">Results</div>
                </a>
                <a href="/admin/admissions" className="bg-purple-500 text-white p-3 rounded text-center hover:bg-purple-600 transition">
                  <div className="text-2xl mb-1">📝</div>
                  <div className="text-sm">Admissions</div>
                </a>
                <a href="/admin/gallery" className="bg-pink-500 text-white p-3 rounded text-center hover:bg-pink-600 transition">
                  <div className="text-2xl mb-1">🖼️</div>
                  <div className="text-sm">Gallery</div>
                </a>
                <a href="/admin/users" className="bg-indigo-500 text-white p-3 rounded text-center hover:bg-indigo-600 transition">
                  <div className="text-2xl mb-1">👥</div>
                  <div className="text-sm">Users</div>
                </a>
                <a href="/admin/settings" className="bg-gray-500 text-white p-3 rounded text-center hover:bg-gray-600 transition">
                  <div className="text-2xl mb-1">⚙️</div>
                  <div className="text-sm">Settings</div>
                </a>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Upcoming Events</h2>
              <div className="space-y-4">
                {dashboardData.upcomingEvents.map(event => (
                  <div key={event.id} className="border-l-4 border-primary pl-4 py-2">
                    <h3 className="font-medium text-gray-800">{event.event}</h3>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">{event.date}</span>
                      <span className="text-sm text-gray-600">{event.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Recent Activities */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activities</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.recentActivities.map(activity => (
                      <tr key={activity.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{activity.activity}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{activity.user}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{activity.time}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            activity.type === 'student' ? 'bg-blue-100 text-blue-800' :
                            activity.type === 'teacher' ? 'bg-green-100 text-green-800' :
                            activity.type === 'notice' ? 'bg-yellow-100 text-yellow-800' :
                            activity.type === 'result' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {activity.type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">System Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-2">Server Status</h3>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Operational</span>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-2">Database</h3>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Connected</span>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-2">Users Online</h3>
                  <div className="text-lg font-semibold text-gray-800">24</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;